import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { WS_SERVER_URL, WS_SOCKET_PATH } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';

const resolveSocketBaseUrl = () => {
  if (WS_SERVER_URL && WS_SERVER_URL.trim()) {
    return WS_SERVER_URL.trim();
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const { protocol, hostname, port, origin } = window.location;
    // In CRA dev mode (usually :3000), connect directly to websocket service.
    if (String(port) === '3000') {
      return `${protocol}//${hostname}:8003`;
    }
    return origin;
  }
  return '';
};

const resolveClientToken = () => {
  const accountId = getActiveAccountId();
  return (
    localStorage.getItem(scopedKey('ws_client_jwt', accountId)) ||
    localStorage.getItem(scopedKey('ws_admin_jwt', accountId)) ||
    ''
  );
};

export const useRealtimeSocket = ({
  enabled = true,
  room = '',
  events = [],
  onEvent = () => {},
  clientName = '',
} = {}) => {
  const [status, setStatus] = useState('idle');
  const [socketId, setSocketId] = useState('');
  const [lastError, setLastError] = useState('');
  const socketRef = useRef(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const eventNames = useMemo(() => {
    const base = ['server:message'];
    const merged = [...base, ...(Array.isArray(events) ? events : [])];
    return [...new Set(merged.filter((entry) => typeof entry === 'string' && entry.trim()))];
  }, [events]);

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      return undefined;
    }
    const baseUrl = resolveSocketBaseUrl();
    if (!baseUrl) {
      setStatus('error');
      setLastError('missing websocket server url');
      return undefined;
    }

    const token = resolveClientToken();
    const authHeaders = getAuthHeadersFromStorage();
    const apiKey = authHeaders['X-API-Key'] || '';

    const socket = io(baseUrl, {
      path: WS_SOCKET_PATH || '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        ...(token ? { token } : {}),
        ...(apiKey ? { apiKey } : {}),
        ...(clientName ? { clientName } : {}),
        ...(typeof window !== 'undefined' && window.location?.pathname
          ? { pagePath: window.location.pathname }
          : {}),
      },
      extraHeaders: Object.keys(authHeaders).length ? authHeaders : undefined,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });
    socketRef.current = socket;
    setStatus('connecting');

    const handleConnect = () => {
      setStatus('connected');
      setSocketId(socket.id || '');
      setLastError('');
      if (room && room.trim()) {
        socket.emit('client:join_room', room.trim());
      }
    };

    const handleDisconnect = (reason) => {
      setStatus('disconnected');
      if (reason) {
        setLastError(String(reason));
      }
    };

    const handleConnectError = (error) => {
      setStatus('error');
      setLastError(error?.message || 'socket connection failed');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    const listeners = eventNames.map((eventName) => {
      const handler = (payload) => {
        onEventRef.current?.(eventName, payload);
      };
      socket.on(eventName, handler);
      return { eventName, handler };
    });

    return () => {
      listeners.forEach(({ eventName, handler }) => {
        socket.off(eventName, handler);
      });
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      if (room && room.trim()) {
        socket.emit('client:leave_room', room.trim());
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [clientName, enabled, eventNames, room]);

  return {
    status,
    socketId,
    lastError,
    isConnected: status === 'connected',
  };
};

export default useRealtimeSocket;
